import { apm } from '@elastic/apm-rum';
import { sleep } from '@sports-utils/timer';
import { defineStore, storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useState } from '@/composable/useState';
import { Api } from '@/core/lib/api';
import { UnexpectedResponseError } from '@/core/lib/apiError';
import type { IAdditionalSettings, ICustomerSettings } from '@/core/lib/businessLogicHelper';
import { refreshAndScheduleAutoRefreshTokens } from '@/core/oddsApi/apollo';
import { formatMTokens } from '@/core/oddsApi/helpers/formatToken';
import type { IMTokens } from '@/interface/IMTokens';
import { OddApiStatus } from '@/interface/enum';
import { useCustomerStore } from '@/store/customerStore';
import { useToggleStore } from '@/store/toggleStore';

const GET_TOKEN_RETRY_COUNT = 5;

// The last attempt is returned as-is so its rejection propagates untouched: wrapping it in a new
// Error would make the UnexpectedResponseError check below (and therefore APM) never fire.
// Sleeping only between attempts also avoids a pointless wait after the final failure.
function withRetry<T>(fn: () => Promise<T>, attempts: number, delay = 0): Promise<T> {
    if (attempts <= 1) return fn();
    return fn().catch(() => sleep(delay).then(() => withRetry(fn, (attempts - 1), delay)));
}

export const useOddsApiStore = defineStore('oddsApi', () => {
    const [authToken, setAuthToken] = useState('');
    const [soccerMOddsTokens, setSoccerMOddsTokens] = useState<IMTokens>({ m: '', b: '', s: '' });
    const [othersMOddsTokens, setOthersMOddsTokens] = useState<IMTokens>({ m: '', b: '', s: '' });
    const [oddsApiStatus, setOddsApiStatus] = useState<OddApiStatus>(OddApiStatus.NO_TOKEN);

    const { isEFootballEnabled, isSimulationEnabled, playerType } = storeToRefs(useCustomerStore());
    const { isCricketEnabled, isInEFootballLeagueBlackList, filteredEFootballLeagueIds } = storeToRefs(useToggleStore());
    const customerSettings = computed<ICustomerSettings>(() => ({
        isEFootballEnabled: isEFootballEnabled.value,
        isSimulationEnabled: isSimulationEnabled.value,
        isCricketEnabled: isCricketEnabled.value,
        playerType: playerType.value,
        isInEFootballLeagueBlackList: isInEFootballLeagueBlackList.value,
    }));
    const additionalSettings = computed<IAdditionalSettings>(() => ({
        filteredEFootballLeagueIds: filteredEFootballLeagueIds.value,
    }));

    async function refreshOddsApiTokens() {
        setOddsApiStatus(OddApiStatus.LOADING);

        try {
            const tokenResponse = await withRetry(() => Api.getOddsApiTokens(), GET_TOKEN_RETRY_COUNT, 1000);
            const { authToken, sm, om } = tokenResponse.data;

            setAuthToken(authToken);
            setSoccerMOddsTokens(formatMTokens(sm));
            setOthersMOddsTokens(formatMTokens(om));
            setOddsApiStatus(OddApiStatus.LOADED);
        } catch (error) {
            setOddsApiStatus(OddApiStatus.ERRORED);

            if (error instanceof UnexpectedResponseError) {
                apm.captureError(error);
            }

            /**
             * Wait for 15 seconds before retrying to avoid
             * getting the token immediately because the
             * server may be down.
             */
            window.setTimeout(() => {
                if (oddsApiStatus.value === OddApiStatus.ERRORED) {
                    clearTokens();
                    refreshAndScheduleAutoRefreshTokens();
                }
            }, 15 * 1000);

            throw new Error(`GetOddsApiToken failed: ${error}`);
        }
    }

    async function clearTokens() {
        setOddsApiStatus(OddApiStatus.NO_TOKEN);
        setAuthToken('');
        setSoccerMOddsTokens({ m: '', b: '', s: '' });
        setOthersMOddsTokens({ m: '', b: '', s: '' });
    }

    return {
        authToken,
        soccerMOddsTokens,
        othersMOddsTokens,
        oddsApiStatus,
        customerSettings,
        additionalSettings,
        refreshOddsApiTokens,
        clearTokens,
    };
});
